import React from 'react';
import { Drawer, DrawerBody, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Tooltip } from '@fluentui/react-components';
import {
    ChevronDown12Regular,
    Delete12Regular,
    Dismiss20Regular,
    Edit12Regular,
    Info12Regular,
    Star16Filled,
    Star16Regular,
} from '@fluentui/react-icons';
import styles from './css/index.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { DeleteTaskComment, GetTaskComment, PatchTask, PatchTaskComment, PostTaskComment } from '../../redux/taskSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { format } from 'date-fns';
import SafeHTML from '../SafeHTML';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import TaskAttachments, { AttachmentUploadLink } from './TaskAttachments';
import TaskAttachmentPreviewPanel from './TaskAttachmentPreviewPanel';
import TaskReferences from './TaskReferences';
import TaskReferencePreviews from './TaskReferencePreviews';
import WorkstreamManagementMenu from './WorkstreamManagementMenu';
import TagManagementMenu from './TagManagementMenu';
import ProjectPhaseManagementMenu from './ProjectPhaseManagementMenu';
import DeadlinePickerMenu from './TimelinePickerMenu';
import TimelineRangePickerMenu from './TimelineRangePickerMenu';
import ModePickerMenu from './ModePickerMenu';
import DurationPickerMenu from './DurationPickerMenu';
import UserAssignmentMenu from './UserAssignmentMenu';
import { isValidExtensionToUpload } from '../../utils/commonUtils';
import { useSuppressThirdPartyWidgets } from '../../hooks/useSuppressThirdPartyWidgets';
import { getInheritedDeadline, getInheritedDuration, getInheritedTimeline } from '../../utils/taskHierarchyUtils';
import {
    formatDuration,
    getStatusDisplayMeta,
    getStatusOptionsForProject,
    getVisibleTaskTags,
    isReservedOffTrackTag,
    isSchedulingMode,
    DURATION_SUFFIX_HELP,
    taskHasChildren,
} from '../../utils/taskSchedulingUtils';

const getStatusInfo = (status, project) => {
    const meta = getStatusDisplayMeta(status, project);
    if (meta) {
        return { bg: meta.bg, color: meta.color, text: meta.label, border: meta.border };
    }
    return { bg: '#FFFFFF', color: '#989BAD', text: 'Not Started' };
};

const getPriorityInfo = priority => {
    const nonePriority = { bg: '#FFFFFF', color: '#989BAD', text: 'None' };
    const priorityMap = {
        HIGH: { bg: '#192BC2', color: 'white', text: 'High' },
        MEDIUM: { bg: '#4361EE', color: 'white', text: 'Medium' },
        LOW: { bg: '#4895EF', color: 'white', text: 'Low' },
        '': nonePriority,
        NONE: nonePriority,
        None: nonePriority,
    };
    return priorityMap[priority] || nonePriority;
};

const formatShortDate = value => {
    if (!value) return '';
    try {
        return format(new Date(value), 'dd/MM/yy');
    } catch {
        return '';
    }
};

const formatActivityDate = value => {
    if (!value) return '';
    try {
        return format(new Date(value), 'dd/MM/yyyy');
    } catch {
        return '';
    }
};

const formatDurationLabel = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return '';
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '';
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return '';
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days < 30) return `${days} day${days === 1 ? '' : 's'}`;
    if (days < 365) {
        const months = Math.floor(days / 30);
        const remainingDays = days - months * 30;
        if (remainingDays === 0) return `${months} month${months === 1 ? '' : 's'}`;
        return `${months} month${months === 1 ? '' : 's'} ${remainingDays} day${remainingDays === 1 ? '' : 's'}`;
    }
    const years = Math.floor(days / 365);
    const remainingDays = days - years * 365;
    const months = Math.floor(remainingDays / 30);
    if (months === 0) return `${years} year${years === 1 ? '' : 's'}`;
    return `${years} year${years === 1 ? '' : 's'} ${months} month${months === 1 ? '' : 's'}`;
};

const FieldPill = ({ children, style, chevronColor, hideChevron = false, title }) => (
    <div className={styles.taskPanelPill} style={style} title={title}>
        <div className={styles.taskPanelPillContent}>{children}</div>
        {!hideChevron && (
            <ChevronDown12Regular
                className={`${styles.taskPanelPillChevron} ${chevronColor === 'white' ? styles.taskPanelPillWhiteChevron : ''}`}
            />
        )}
    </div>
);

const TaskDetailsSidebar = ({
    open,
    onClose,
    task,
    onNavigateTask,
    hasPrev = false,
    hasNext = false,
    focusRequest = null,
    onFocusRequestHandled,
    onRequestDurationChange,
    onRequestTimelineChange,
    onRequestModeChange,
    onRequestDeadlineChange,
    onRequestStatusChange,
    customOrder = null,
}) => {
    const dispatch = useDispatch();
    const { taskComments, taskCommentBtnLoading, taskData } = useSelector(state => state.task);
    const { selectedProject } = useSelector(state => state.projectData);
    const schedulingEnabled = isSchedulingMode(selectedProject);
    const statusOptions = getStatusOptionsForProject(selectedProject);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const [comment, setComment] = React.useState('');
    const quillRef = React.useRef(null);
    const [editingCommentId, setEditingCommentId] = React.useState(null);
    const [editingCommentText, setEditingCommentText] = React.useState('');
    const editQuillRef = React.useRef(null);
    const [stagedCommentFiles, setStagedCommentFiles] = React.useState([]);
    const [previewAttachment, setPreviewAttachment] = React.useState(null);
    const [previewLink, setPreviewLink] = React.useState(null);
    const [editingTitle, setEditingTitle] = React.useState(false);
    const [titleDraft, setTitleDraft] = React.useState('');
    const titleInputRef = React.useRef(null);
    const [description, setDescription] = React.useState('');
    const [descriptionSaving, setDescriptionSaving] = React.useState(false);
    const descriptionQuillRef = React.useRef(null);

    const liveTask = React.useMemo(() => {
        if (!task?.id) return task;
        return taskData?.find(item => item.id === task.id) || task;
    }, [task, taskData]);

    useSuppressThirdPartyWidgets(open);

    React.useEffect(() => {
        if (liveTask?.id) dispatch(GetTaskComment({ taskId: liveTask.id }));
    }, [liveTask?.id, dispatch]);

    React.useEffect(() => {
        setStagedCommentFiles([]);
        setComment('');
        setPreviewAttachment(null);
        setPreviewLink(null);
        setEditingTitle(false);
        setTitleDraft('');
        setEditingCommentId(null);
        setEditingCommentText('');
        setDescription(liveTask?.description || '');
    }, [liveTask?.id]);

    React.useEffect(() => {
        if (editingTitle) titleInputRef.current?.focus();
    }, [editingTitle]);

    const handleClose = () => {
        setPreviewAttachment(null);
        setPreviewLink(null);
        onClose?.();
    };

    const patchTask = async body => {
        if (!liveTask?.id) return;
        await dispatch(
            PatchTask({
                body,
                id: liveTask.id,
                suppressToast: true,
            }),
        );
    };

    const normalizeDescriptionValue = value => {
        if (!value || value === '<p><br></p>' || !value.trim()) return null;
        return value;
    };

    const saveDescription = async value => {
        if (!liveTask?.id) return;
        const normalized = normalizeDescriptionValue(value);
        const current = normalizeDescriptionValue(liveTask?.description || '');
        if (normalized === current) return;

        setDescriptionSaving(true);
        try {
            await patchTask({ description: normalized });
        } finally {
            setDescriptionSaving(false);
        }
    };

    const handleDescriptionChange = value => {
        setDescription(value === '<p><br></p>' ? '' : value);
    };

    const handleDescriptionSave = async () => {
        if (descriptionSaving) return;
        await saveDescription(description);
    };

    const handleSubmit = async () => {
        const hasCommentText = comment && comment !== '<p><br></p>' && comment.trim();
        if (!hasCommentText && stagedCommentFiles.length === 0) return;

        await dispatch(
            PostTaskComment({
                body: {
                    task: liveTask?.id,
                    comment: hasCommentText ? comment : '',
                    created_by: user?.id,
                },
                files: stagedCommentFiles.length > 0 ? stagedCommentFiles : undefined,
            }),
        );
        setComment('');
        setStagedCommentFiles([]);
        if (quillRef.current) {
            quillRef.current.getEditor().setText('');
        }
    };

    const handleEditComment = commentItem => {
        setEditingCommentId(commentItem.id);
        setEditingCommentText(commentItem.comment);
    };

    const handleSaveEdit = async () => {
        if (editingCommentText.trim() && editingCommentId) {
            dispatch(
                PatchTaskComment({
                    commentId: editingCommentId,
                    body: { comment: editingCommentText },
                }),
            );
            setEditingCommentId(null);
            setEditingCommentText('');
        }
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingCommentText('');
    };

    const handleGetProjectName = (id, onlyName) => {
        let userData = projectUsersAll.find(item => item.value.user_id === id);
        if (!userData && user?.id === id) {
            userData = { value: { ...user } };
        }

        return onlyName ? (
            <span> {userData?.value?.full_name}</span>
        ) : (
            <div className="d-flex align-items-center">
                <div className={styles.smallAvatar} style={{ background: userData?.color }}>
                    {getProfileNameComponent(userData?.value?.full_name)}
                </div>
                <span style={{ maxWidth: 120 }} className={styles.smallFont + ' m-0 ms-2 text-truncate'}>
                    {userData?.value?.full_name}
                </span>
            </div>
        );
    };

    const getProfileNameComponent = fullName => {
        if (!fullName) return '';
        const names = fullName.split(' ');
        if (names.length >= 2) {
            return (names[0][0] + names[1][0]).toUpperCase();
        }
        return fullName[0]?.toUpperCase() || '';
    };

    const assigneeName = React.useMemo(() => {
        if (liveTask?.assigned_to_name) return liveTask.assigned_to_name;
        if (liveTask?.assigned_to) {
            const userData = projectUsersAll.find(item => item.value.user_id === liveTask.assigned_to);
            return userData?.value?.full_name || null;
        }
        if (liveTask?.assigned_to_offline) return liveTask.assigned_to_offline;
        return null;
    }, [liveTask?.assigned_to, liveTask?.assigned_to_name, liveTask?.assigned_to_offline, projectUsersAll]);

    const plainTitle = liveTask?.task ? liveTask.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : '';

    const tagsDisplay = React.useMemo(
        () => getVisibleTaskTags(liveTask).join(', '),
        [liveTask?.tags, liveTask?.tag_repr],
    );

    const inheritedTimeline = React.useMemo(
        () => getInheritedTimeline(liveTask, taskData || []),
        [liveTask, taskData],
    );
    const inheritedDeadline = React.useMemo(
        () => getInheritedDeadline(liveTask, taskData || []),
        [liveTask, taskData],
    );
    const inheritedDuration = React.useMemo(
        () => getInheritedDuration(liveTask, taskData || []),
        [liveTask, taskData],
    );
    const isParentTask = taskHasChildren(liveTask, taskData || []);
    const scheduleLocked = schedulingEnabled && (inheritedTimeline.isInherited || isParentTask);

    const timelineLabel = React.useMemo(() => {
        const start = formatShortDate(inheritedTimeline?.start_date || liveTask?.start_date);
        const end = formatShortDate(inheritedTimeline?.end_date || liveTask?.end_date);
        const placeholder = 'dd/MM/yyyy';
        if (start && end) return `${start} – ${end}`;
        if (start) return `${start} – ${placeholder}`;
        if (end) return `${placeholder} – ${end}`;
        return '';
    }, [inheritedTimeline?.start_date, inheritedTimeline?.end_date, liveTask?.start_date, liveTask?.end_date]);

    // Phase 1: parent Deadline stays editable even when timeline is derived
    const deadlineLabel = formatShortDate(liveTask?.deadline || inheritedDeadline?.deadline);

    const durationLabel = schedulingEnabled
        ? scheduleLocked
            ? formatDuration(liveTask?.duration ?? inheritedDuration?.duration)
            : formatDuration(liveTask?.duration) || formatDurationLabel(inheritedTimeline?.start_date, inheritedTimeline?.end_date)
        : formatDurationLabel(liveTask?.start_date, liveTask?.end_date);
    const statusInfo = getStatusInfo(liveTask?.status, selectedProject);
    const priorityInfo = getPriorityInfo(liveTask?.priority);
    const activityItems = React.useMemo(() => {
        const items = [];
        if (liveTask?.created) {
            items.push({
                id: 'created',
                text: `${liveTask.created_by_name || 'Someone'} created this task`,
                date: formatActivityDate(liveTask.created),
            });
        }
        if (liveTask?.last_updated && liveTask.last_updated !== liveTask.created) {
            items.push({
                id: 'updated',
                text: `${liveTask.last_updated_by_name || 'Someone'} updated this task`,
                date: formatActivityDate(liveTask.last_updated),
            });
        }
        if (Array.isArray(liveTask?.deadline_history)) {
            liveTask.deadline_history.forEach((entry, index) => {
                items.push({
                    id: `deadline-${index}`,
                    text: `${entry.changed_by || 'System'} changed deadline to ${entry.deadline ? formatActivityDate(entry.deadline) : 'none'}`,
                    date: formatActivityDate(entry.updated_on),
                });
            });
        }
        if (Array.isArray(liveTask?.activity_history)) {
            liveTask.activity_history.forEach((entry, index) => {
                items.push({
                    id: `activity-${index}`,
                    text: `${entry.changed_by || 'Someone'} changed ${entry.field || 'field'}${
                        entry.new != null
                            ? ` to ${entry.field === 'duration' ? formatDuration(entry.new) || entry.new : entry.new}`
                            : ''
                    }`,
                    date: formatActivityDate(entry.updated_on),
                });
            });
        }
        return items;
    }, [liveTask]);

    const handleStarToggle = async () => {
        if (!liveTask?.id) return;

        if (!liveTask.starred) {
            // Only one key task per project — unstar any other starred tasks first
            const otherStarred = (taskData || []).filter(t => t.id !== liveTask.id && t.starred);
            for (const other of otherStarred) {
                await dispatch(PatchTask({ body: { starred: false }, id: other.id, suppressToast: true }));
            }
            await patchTask({ starred: true });
        } else {
            await patchTask({ starred: false });
        }
    };

    const handleTitleSave = async () => {
        const next = (titleDraft || '').trim();
        setEditingTitle(false);
        if (!next || next === plainTitle) return;
        await patchTask({ task: next });
    };

    const handleStatusChange = async newStatus => {
        if (onRequestStatusChange) {
            onRequestStatusChange(liveTask, newStatus);
            return;
        }

        const body = { status: newStatus };
        if ((newStatus === 'COMPLETED' || newStatus === 'COMPLETE') && liveTask?.starred) {
            body.starred = false;
        }
        await patchTask(body);
    };

    const handlePriorityChange = async newPriority => {
        await patchTask({ priority: newPriority });
    };

    const handleTagSelect = async tag => {
        const tagName = tag?.name || '';
        if (isReservedOffTrackTag(tagName)) return;

        const existingTags = Array.isArray(liveTask?.tags)
            ? liveTask.tags
            : liveTask?.tag_repr
            ? [liveTask.tag_repr]
            : [];
        const reserved = existingTags.filter(t => isReservedOffTrackTag(t));
        const userTags = existingTags.filter(t => !isReservedOffTrackTag(t));

        let nextUserTags;
        if (!tagName) {
            nextUserTags = [];
        } else if (userTags.includes(tagName)) {
            nextUserTags = userTags.filter(t => t !== tagName);
        } else {
            nextUserTags = [...userTags, tagName];
        }

        await patchTask({ tags: [...nextUserTags, ...reserved] });
    };

    const handleWorkstreamSelect = async workstream => {
        await patchTask({ workstream_repr: workstream?.name || '' });
    };

    const handleProjectPhaseChange = async phase => {
        await patchTask({ project_phase: phase?.name || null });
    };

    const canSend = (comment && comment !== '<p><br></p>' && comment.trim()) || stagedCommentFiles.length > 0;
    const canSaveDescription =
        normalizeDescriptionValue(description) !== normalizeDescriptionValue(liveTask?.description || '');
    const descriptionActionLabel = liveTask?.description ? 'Update' : 'Save';

    const taskDetailsContent = (
        <div className={styles.taskPanelV2}>
            <div className={styles.taskPanelNav}>
                <button
                    type="button"
                    className={`${styles.taskPanelNavBtn} ${hasPrev ? styles.taskPanelNavBtnEnabled : ''}`}
                    disabled={!hasPrev}
                    onClick={() => hasPrev && onNavigateTask?.('prev')}>
                    <span className={styles.taskPanelNavArrow}>←</span>
                    Previous task
                </button>
                <button
                    type="button"
                    className={`${styles.taskPanelNavBtn} ${hasNext ? styles.taskPanelNavBtnEnabled : ''}`}
                    disabled={!hasNext}
                    onClick={() => hasNext && onNavigateTask?.('next')}>
                    Next task
                    <span className={styles.taskPanelNavArrow}>→</span>
                </button>
            </div>

            <div className={styles.taskPanelHeader}>
                <div className={styles.taskPanelHeaderTop}>
                    <p className={styles.taskPanelSectionLabel}>Task</p>
                    <button type="button" className={styles.taskPanelCloseBtn} onClick={handleClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
                <div className={styles.taskPanelTitleRow}>
                    <button type="button" className={styles.taskPanelStarBtn} onClick={handleStarToggle} aria-label="Toggle star">
                        {liveTask?.starred ? <Star16Filled style={{ color: '#FFD700' }} /> : <Star16Regular style={{ color: '#989BAD' }} />}
                    </button>
                    {editingTitle ? (
                        <input
                            ref={titleInputRef}
                            className={styles.taskPanelTitleInput}
                            value={titleDraft}
                            onChange={e => setTitleDraft(e.target.value)}
                            onBlur={handleTitleSave}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleTitleSave();
                                } else if (e.key === 'Escape') {
                                    setEditingTitle(false);
                                    setTitleDraft(plainTitle);
                                }
                            }}
                        />
                    ) : (
                        <p
                            className={styles.taskPanelTitle}
                            onClick={() => {
                                setTitleDraft(plainTitle);
                                setEditingTitle(true);
                            }}>
                            {plainTitle || 'Untitled task'}
                        </p>
                    )}
                </div>
            </div>

            <div className={styles.taskPanelMeta}>
                <div className={styles.taskPanelMetaRow}>
                    <div className={styles.taskPanelField}>
                        <p className={styles.taskPanelFieldLabel}>Priority</p>
                        <Menu>
                            <MenuTrigger>
                                <div>
                                    <FieldPill
                                        style={{
                                            background: priorityInfo.bg,
                                            color: priorityInfo.color,
                                            border: priorityInfo.bg === '#FFFFFF' ? '1px solid #DDDEE4' : undefined,
                                        }}
                                        chevronColor={priorityInfo.color === 'white' ? 'white' : undefined}>
                                        <span className={styles.taskPanelPillText} style={{ color: priorityInfo.color }}>
                                            {priorityInfo.text}
                                        </span>
                                    </FieldPill>
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '117px', minWidth: '117px' }}>
                                <MenuList>
                                    <MenuItem className={styles.priorityMenuItem} onClick={() => handlePriorityChange('HIGH')} style={{ backgroundColor: '#192BC2', color: 'white' }}>
                                        High
                                    </MenuItem>
                                    <MenuItem className={styles.priorityMenuItem} onClick={() => handlePriorityChange('MEDIUM')} style={{ backgroundColor: '#4361EE', color: 'white' }}>
                                        Medium
                                    </MenuItem>
                                    <MenuItem className={styles.priorityMenuItem} onClick={() => handlePriorityChange('LOW')} style={{ backgroundColor: '#4895EF', color: 'white' }}>
                                        Low
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.priorityMenuItem}
                                        onClick={() => handlePriorityChange('')}
                                        style={{ backgroundColor: '#FFFFFF', color: '#989BAD', border: '1px solid #DDDEE4' }}>
                                        None
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                    <div className={styles.taskPanelField}>
                        <p className={styles.taskPanelFieldLabel}>Deadline</p>
                        <DeadlinePickerMenu
                            task={liveTask}
                            onRequestDeadlineChange={
                                onRequestDeadlineChange
                                    ? ({ deadline }) => onRequestDeadlineChange(liveTask, { deadline })
                                    : undefined
                            }>
                            <div>
                                <FieldPill>
                                    <span className={styles.taskPanelPillText}>{deadlineLabel || 'Set deadline'}</span>
                                </FieldPill>
                            </div>
                        </DeadlinePickerMenu>
                    </div>
                </div>

                <div className={styles.taskPanelMetaRow}>
                    <div className={styles.taskPanelField}>
                        <p className={styles.taskPanelFieldLabel}>Timeline</p>
                        {scheduleLocked ? (
                            <FieldPill title="Timeline is calculated from subtasks">
                                <span className={styles.taskPanelPillText}>{timelineLabel || '—'}</span>
                            </FieldPill>
                        ) : (
                            <TimelineRangePickerMenu
                                task={liveTask}
                                onRequestTimelineChange={onRequestTimelineChange}
                                schedulingConfirmRequired={schedulingEnabled}>
                                <div>
                                    <FieldPill>
                                        <span className={styles.taskPanelPillText}>{timelineLabel || 'Set timeline'}</span>
                                    </FieldPill>
                                </div>
                            </TimelineRangePickerMenu>
                        )}
                    </div>
                    <div className={styles.taskPanelField}>
                        <p className={`${styles.taskPanelFieldLabel} ${styles.durationHeaderLabel}`}>
                            Duration
                            <Tooltip relationship="description" positioning="above" withArrow content={DURATION_SUFFIX_HELP}>
                                <span className={styles.durationHeaderInfo} role="img" tabIndex={0} aria-label="Duration format">
                                    <Info12Regular />
                                </span>
                            </Tooltip>
                        </p>
                        {schedulingEnabled && !scheduleLocked ? (
                            <DurationPickerMenu
                                durationMinutes={liveTask?.duration}
                                durationLabel={durationLabel}
                                placeholder="Set duration"
                                onSave={async parsed => {
                                    if (onRequestDurationChange) {
                                        onRequestDurationChange(liveTask, parsed);
                                        return;
                                    }
                                    await patchTask({ duration: parsed });
                                }}>
                                <div>
                                    <FieldPill>
                                        <span className={styles.taskPanelPillText}>{durationLabel || 'Set duration'}</span>
                                    </FieldPill>
                                </div>
                            </DurationPickerMenu>
                        ) : scheduleLocked ? (
                            <FieldPill title="Duration is calculated from subtasks">
                                <span className={styles.taskPanelPillText}>{durationLabel || '—'}</span>
                            </FieldPill>
                        ) : (
                            <TimelineRangePickerMenu task={liveTask}>
                                <div>
                                    <FieldPill>
                                        <span className={styles.taskPanelPillText}>{durationLabel || '—'}</span>
                                    </FieldPill>
                                </div>
                            </TimelineRangePickerMenu>
                        )}
                    </div>
                    {schedulingEnabled && (
                        <div className={styles.taskPanelField}>
                            <p className={styles.taskPanelFieldLabel}>Mode</p>
                            <ModePickerMenu
                                task={liveTask}
                                allTasks={taskData || []}
                                scheduleTasks={taskData || []}
                                customOrder={customOrder}
                                readOnly={scheduleLocked}
                                readOnlyReason={isParentTask ? 'parent' : null}
                                onModeSelect={(t, mode) => onRequestModeChange && onRequestModeChange(t, mode)}>
                                <div>
                                    <FieldPill>
                                        <span className={styles.taskPanelPillText}>
                                            {scheduleLocked ? '—' : `${liveTask?.mode || 'FS'} ▾`}
                                        </span>
                                    </FieldPill>
                                </div>
                            </ModePickerMenu>
                        </div>
                    )}
                </div>

                <div className={styles.taskPanelMetaRow}>
                    <div className={styles.taskPanelField}>
                        <p className={styles.taskPanelFieldLabel}>Status</p>
                        <Menu>
                            <MenuTrigger disableButtonEnhancement={scheduleLocked}>
                                <div>
                                    <FieldPill
                                        style={{
                                            background: statusInfo.bg,
                                            color: statusInfo.color,
                                            border: statusInfo.bg === '#FFFFFF' ? '1px solid #DDDEE4' : undefined,
                                            cursor: scheduleLocked ? 'default' : 'pointer',
                                        }}
                                        chevronColor={statusInfo.color === 'white' ? 'white' : undefined}
                                        title={scheduleLocked ? 'Status is calculated from subtasks' : undefined}>
                                        <span className={styles.taskPanelPillText} style={{ color: statusInfo.color }}>
                                            {statusInfo.text}
                                        </span>
                                    </FieldPill>
                                </div>
                            </MenuTrigger>
                            {!scheduleLocked && (
                                <MenuPopover style={{ width: '118px', minWidth: '118px' }}>
                                    <MenuList>
                                        {statusOptions.map(opt => (
                                            <MenuItem
                                                key={opt.value}
                                                className={styles.statusMenuItem}
                                                onClick={() => handleStatusChange(opt.value)}
                                                style={{
                                                    backgroundColor: opt.bg,
                                                    color: opt.color,
                                                    border: opt.border,
                                                }}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            )}
                        </Menu>
                    </div>
                    <div className={styles.taskPanelField}>
                        <p className={styles.taskPanelFieldLabel}>Owner</p>
                        <UserAssignmentMenu task={liveTask}>
                            <div>
                                <FieldPill>
                                    {assigneeName ? (
                                        <>
                                            <div
                                                className={styles.customAvatar}
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    backgroundColor: '#8E2DE2',
                                                    fontSize: '9px',
                                                    margin: 0,
                                                    flexShrink: 0,
                                                }}
                                                title={assigneeName}>
                                                {getProfileNameComponent(assigneeName)}
                                            </div>
                                            <span className={styles.taskPanelPillText}>{assigneeName}</span>
                                        </>
                                    ) : (
                                        <span className={styles.taskPanelPillText}>Unassigned</span>
                                    )}
                                </FieldPill>
                            </div>
                        </UserAssignmentMenu>
                    </div>
                </div>

                <div className={styles.taskPanelField}>
                    <p className={styles.taskPanelFieldLabel}>Workstream</p>
                    <WorkstreamManagementMenu
                        taskData={taskData}
                        selectedWorkstreamName={liveTask?.workstream_repr}
                        onWorkstreamSelect={handleWorkstreamSelect}>
                        <div>
                            <FieldPill>
                                <span className={styles.taskPanelPillText}>{liveTask?.workstream_repr || 'Select workstream'}</span>
                            </FieldPill>
                        </div>
                    </WorkstreamManagementMenu>
                </div>

                <div className={styles.taskPanelField}>
                    <p className={styles.taskPanelFieldLabel}>Tag</p>
                    <TagManagementMenu
                        taskData={taskData}
                        taskWorkstreamName={liveTask?.workstream_repr}
                        selectedTagName={tagsDisplay}
                        onTagSelect={handleTagSelect}>
                        <div>
                            <FieldPill>
                                <span className={styles.taskPanelPillText}>{tagsDisplay || 'Select tag'}</span>
                            </FieldPill>
                        </div>
                    </TagManagementMenu>
                </div>

                <div className={styles.taskPanelField}>
                    <p className={styles.taskPanelFieldLabel}>Project Phase</p>
                    <ProjectPhaseManagementMenu
                        taskData={taskData}
                        selectedPhaseName={liveTask?.project_phase}
                        onPhaseSelect={handleProjectPhaseChange}>
                        <div>
                            <FieldPill>
                                <span className={styles.taskPanelPillText}>{liveTask?.project_phase || 'Select project phase'}</span>
                            </FieldPill>
                        </div>
                    </ProjectPhaseManagementMenu>
                </div>
            </div>

            <div className={styles.taskPanelDivider} />

            <div className={styles.taskPanelSection}>
                <p className={styles.taskPanelSectionLabel}>Task Description</p>
                <div className={styles.commentComposer}>
                    <ReactQuill
                        ref={descriptionQuillRef}
                        placeholder="Add a description"
                        modules={{
                            toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                        }}
                        theme="snow"
                        className={styles.commentComposerEditor}
                        value={description}
                        onChange={handleDescriptionChange}
                    />
                    <div className={styles.commentComposerActions}>
                        <div className={styles.commentComposerActionsLeft} />
                        <button
                            type="button"
                            className={styles.taskPanelSendBtn}
                            onClick={handleDescriptionSave}
                            disabled={!canSaveDescription || descriptionSaving}>
                            {descriptionActionLabel}{' '}
                            {descriptionSaving ? <span className="spinner-border spinner-border-sm ms-1" /> : null}
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.taskPanelDivider} />

            <div className={styles.taskPanelSection}>
                <p className={styles.taskPanelSectionLabel}>References</p>
                {liveTask?.id && (
                    <TaskReferences
                        taskId={liveTask.id}
                        hideLabel
                        onOpenLink={setPreviewLink}
                        scrollToRequest={focusRequest?.type === 'references-view' ? focusRequest.nonce : null}
                        openPickerRequest={focusRequest?.type === 'references' ? focusRequest.nonce : null}
                        onOpenPickerRequestHandled={onFocusRequestHandled}
                    />
                )}
            </div>

            <div className={styles.taskPanelDivider} />

            <div className={styles.taskPanelSection}>
                <p className={styles.taskPanelSectionLabel}>Attachments</p>
                {liveTask?.id && (
                    <TaskAttachments
                        mode="task"
                        taskId={liveTask.id}
                        label=""
                        onView={setPreviewAttachment}
                        activeAttachmentId={previewAttachment?.id}
                        scrollToRequest={focusRequest?.type === 'attachments-view' ? focusRequest.nonce : null}
                        openUploadRequest={focusRequest?.type === 'attachments' ? focusRequest.nonce : null}
                        onOpenUploadRequestHandled={onFocusRequestHandled}
                    />
                )}
            </div>

            <div className={styles.taskPanelDivider} />

            <div className={styles.taskPanelSection}>
                <p className={styles.taskPanelSectionLabel}>Comments</p>

                {taskComments?.length > 0 && (
                    <div className={styles.taskPanelCommentsList}>
                        {taskComments.map(item => (
                            <div key={item?.id}>
                                {editingCommentId === item?.id ? (
                                    <div>
                                        <ReactQuill
                                            key={editingCommentId}
                                            ref={editQuillRef}
                                            placeholder="Edit comment"
                                            modules={{
                                                toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                                            }}
                                            theme="snow"
                                            className="mt-1"
                                            value={editingCommentText}
                                            onChange={t => {
                                                if (t === '<p><br></p>') setEditingCommentText('');
                                                else setEditingCommentText(t);
                                            }}
                                        />
                                        <div className="mt-2 d-flex gap-2">
                                            <button onClick={handleSaveEdit} disabled={!editingCommentText.trim()} type="button" className="btn btn-primary btn-sm">
                                                Save
                                            </button>
                                            <button onClick={handleCancelEdit} type="button" className="btn btn-secondary btn-sm">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {item?.comment ? <SafeHTML html={item?.comment} className={styles.commentText} tag="p" /> : null}
                                        {(item?.attachments?.length > 0 || item?.created_by === user?.id) && (
                                            <TaskAttachments
                                                mode="comment"
                                                commentId={item.id}
                                                attachments={item.attachments}
                                                showUpload={item.created_by === user?.id}
                                                compact
                                                onView={setPreviewAttachment}
                                                activeAttachmentId={previewAttachment?.id}
                                            />
                                        )}
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className={styles.sidebarWsTagLabel + ' mb-0 mt-1'}>
                                                {handleGetProjectName(item?.created_by, true)}{' '}
                                                {item?.created && format(new Date(item?.created), 'dd/MM/yyyy')}
                                            </div>
                                            {item?.created_by === user?.id && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <DoubleConfirmationModal
                                                        message="Deleting a comment will permanently remove it from the Task."
                                                        handleAction={() => {
                                                            if (taskCommentBtnLoading) return;
                                                            if (!item?.id || !liveTask?.id) return;
                                                            if (editingCommentId === item.id) {
                                                                setEditingCommentId(null);
                                                                setEditingCommentText('');
                                                            }
                                                            dispatch(
                                                                DeleteTaskComment({
                                                                    commentId: item.id,
                                                                    taskId: liveTask.id,
                                                                }),
                                                            );
                                                        }}>
                                                        <Delete12Regular
                                                            cursor="pointer"
                                                            color="var(--Brand-Secondary)"
                                                            style={{
                                                                opacity: taskCommentBtnLoading ? 0.5 : 1,
                                                                pointerEvents: taskCommentBtnLoading ? 'none' : 'auto',
                                                            }}
                                                        />
                                                    </DoubleConfirmationModal>
                                                    <Edit12Regular
                                                        cursor="pointer"
                                                        onClick={() => handleEditComment(item)}
                                                        color="var(--Brand-Secondary)"
                                                        style={{
                                                            opacity: taskCommentBtnLoading ? 0.5 : 1,
                                                            pointerEvents: taskCommentBtnLoading ? 'none' : 'auto',
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className={styles.commentComposer}>
                    <ReactQuill
                        ref={quillRef}
                        placeholder="Add a comment"
                        modules={{
                            toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
                        }}
                        theme="snow"
                        className={styles.commentComposerEditor}
                        value={comment}
                        onChange={t => {
                            if (t === '<p><br></p>') setComment('');
                            else setComment(t);
                        }}
                    />
                    <TaskAttachments mode="staged" stagedFiles={stagedCommentFiles} onStagedFilesChange={setStagedCommentFiles} compact />
                    <div className={styles.commentComposerActions}>
                        <div className={styles.commentComposerActionsLeft}>
                            <AttachmentUploadLink
                                compact
                                label="Attach file"
                                onFiles={files => {
                                    const merged = [...stagedCommentFiles];
                                    files.forEach(f => {
                                        const ext = f.name.split('.').pop()?.toLowerCase();
                                        if (!isValidExtensionToUpload(ext)) return;
                                        if (!merged.some(existing => existing.name === f.name && existing.size === f.size)) {
                                            merged.push(f);
                                        }
                                    });
                                    setStagedCommentFiles(merged);
                                }}
                            />
                        </div>
                        <button type="button" className={styles.taskPanelSendBtn} onClick={handleSubmit} disabled={!canSend || taskCommentBtnLoading}>
                            Send {taskCommentBtnLoading ? <span className="spinner-border spinner-border-sm ms-1" /> : null}
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.taskPanelDivider} />

            <div className={styles.taskPanelSection}>
                <p className={styles.taskPanelSectionLabel}>Activity</p>
                <div className={styles.taskPanelActivityList}>
                    {activityItems.length > 0 ? (
                        activityItems.map(item => (
                            <div key={item.id} className={styles.taskPanelActivityItem}>
                                <span className={styles.taskPanelActivityBullet} />
                                <p className={styles.taskPanelActivityText}>
                                    {item.text}
                                    {item.date ? (
                                        <>
                                            {' · '}
                                            <span className={styles.taskPanelActivityDate}>{item.date}</span>
                                        </>
                                    ) : null}
                                </p>
                            </div>
                        ))
                    ) : (
                        <p className={styles.taskPanelActivityText}>
                            <span className={styles.taskPanelActivityDate}>No activity yet</span>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <Drawer
                type="overlay"
                position="end"
                open={open}
                onOpenChange={(e, data) => {
                    if (!data.open) handleClose();
                }}
                size="medium">
                <DrawerBody style={{ padding: 0 }}>
                    <div className={styles.taskDrawerDetails}>{liveTask ? taskDetailsContent : null}</div>
                </DrawerBody>
            </Drawer>
            <TaskAttachmentPreviewPanel attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
            <TaskReferencePreviews previewLink={previewLink} onClose={() => setPreviewLink(null)} />
        </>
    );
};

export default TaskDetailsSidebar;
